import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, Image, InputGroup, Table, Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import nodataimage from "../../../assets/nodataimage.svg";
import { faSearch, faChevronDown, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageHeading from "components/shared/PageHeading";
import moment from "moment";
import { createSubmission, generateSebFile } from "services/Student/Submission.services";
import DynamicModal from "components/modals/DynamicModal";
import SebInstructionsModal from "components/modals/SebInstructionsModal";
import { getAllSubject } from "services/Teacher/Exercise.service";
import { getAllStudentAssignment } from "services/Teacher/Assignments.service";
import { useTranslation } from "react-i18next";
import { SocketContext } from "utils/SocketContext";

const ASSIGNMENT_REFRESH_TYPES = new Set([
  "assignment-exercise-assigned",
  "assignment-exercise-marked",
]);

const sortOptions = ["A to Z","Z to A", "Latest", "Oldest"]
const statusOptions = ["All","pending","submitted", "marked"]

const Assignments = ({ source = null, classId = null }) => {
  const { t } = useTranslation();
  const socket = useContext(SocketContext);

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;
  // Larger page size than the old pager used, so each scroll fetch is worthwhile.
  const initialLimit = parseInt(searchParams.get("limit"), 10) || 15;
  const initialSearchQuery = searchParams.get("search") || "";

  const [assignments, setAssignments] = useState([]);
  const [loadingSeb, setLoadingSeb] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const loadMoreRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showSEBModal, setShowSEBModal] = useState(false);
  const [showTerminatedModal, setShowTerminatedModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [currentSubId, setCurrentSubId] = useState(null);
  const [filters, setFilters] = useState({});
  const [show, setShow] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedSort, setSelectedSort] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const limit = initialLimit;

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(!show);
  
  // Page 1 replaces the list (fresh load / filter change); later pages append,
  // which is what turns the pager into an infinite scroll.
  const AllExercises = async (page = 1, limit = 5, status, subject = "", sort = "", search = "") => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const response = await getAllStudentAssignment(
        page,
        limit,
        status,
        subject,
        sort,
        search,
        source,
        classId
      );
      if (response.data && response.data.results) {
        // The backend now returns parent assignments with childAssignments array
        const incoming = response.data.results?.assignments || [];
        setAssignments((prev) => {
          if (isFirstPage) return incoming;
          // Guard against duplicates if a page is fetched twice.
          const seen = new Set(prev.map((a) => a._id));
          return [...prev, ...incoming.filter((a) => !seen.has(a._id))];
        });
        setTotalPages(response.data.results.totalPages || 1);
        setCurrentPage(page);
        setSelectedSubject(subject);
        setSelectedStatus(status);
        setSelectedSort(sort);
        setSearch(search);
      } else if (isFirstPage) {
        setAssignments([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching assignments:", error);
      if (isFirstPage) {
        setAssignments([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchSubject = async () => {
    try {
      const response = await getAllSubject();
      const formattedSubjects = response?.data?.results?.subject?.map(subject => ({
        value: subject._id,
        label: subject.title,
      }));
      setSubjects(formattedSubjects);
    } catch (error) {
      console.error("Error fetching school details:", error);
    }
  };

  useEffect(() => {
    AllExercises(1, limit, selectedStatus, selectedSubject, selectedSort, search);
    fetchSubject();
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = (notification) => {
      if (ASSIGNMENT_REFRESH_TYPES.has(notification?.type)) {
        AllExercises(1, limit, selectedStatus, selectedSubject, selectedSort, search);
      }
    };
    socket.on("new-notification", handler);
    return () => {
      socket.off("new-notification", handler);
    };
  }, [socket, limit, selectedStatus, selectedSubject, selectedSort, search]);

  const handleRefresh = () => {
    AllExercises(currentPage, limit, selectedStatus, selectedSubject, selectedSort, search);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(search);
    setCurrentPage(1);
  };

  const handleDownloadSebFile = async () => {
    setLoadingSeb(true);
    try {
      const response = await generateSebFile(currentId);

      if (response && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `quiz_${currentId}.seb`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setShowSEBModal(false);
        setLoadingSeb(false);
      } else {
        toast.error("Error While Downloading");
        setLoadingSeb(false);
      }
    } catch (error) {
      toast.error("Error While Downloading");
      console.error("Failed to download SEB file:", error);
      setLoadingSeb(false);
    }
  };

  const handlePreview = (assignment) => {
    setSelectedAssignment(assignment);
    setShowPreviewModal(true);
  };

  const handleAction = (status, id, subId, type, studySetId, safeBrowser) => {
    setCurrentStatus(status);
    setCurrentId(id);
    setCurrentSubId(subId);
    if (status === "terminated") {
      setShowTerminatedModal(true);
    } else if (["pending", "in-progress", "revise-needed", "completed"].includes(status)) {
      if (type === "studySet") {
        navigate(`/vocabulary/assigned/${studySetId}?assignment=${id}`);
      }
      if (safeBrowser) {
        setShowSEBModal(true);
        handleConfirm(safeBrowser);
      } else {
        setShowModal(true);
      }
    } else {
      switch (status) {
        case "submitted":
          handleSubmittedAction(subId);
          break;
        case "marked":
          handleMarkedAction(subId);
          break;
        default:
          console.log("Unknown action");
      }
    }
  };

  const handleConfirm = (safeBrowser) => {
    switch (currentStatus) {
      case "pending":
        handlePendingAction(currentId, safeBrowser);
        break;
      case "in-progress":
        handleInProgressAction(currentSubId, safeBrowser);
        break;
      case "revise-needed":
        handleReviseAction(currentId, currentSubId, safeBrowser);
        break;
      default:
        console.log("Unknown action");
    }
    setShowModal(false);
  };

  const handlePendingAction = async (id, safeBrowser) => {
    if (safeBrowser) {
      setShowSEBModal(true);
    } else {
      try {
        const response = await createSubmission({
          assignmentId: id,
        });
        navigate(`/quiz/attempt/${response.data?.result?._id}`);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleInProgressAction = async (id, safeBrowser) => {
    if (safeBrowser) {
      setShowSEBModal(true);
    } else {
      navigate(`/quiz/attempt/${id}`);
    }
  };

  const handleReviseAction = async (id, subId, safeBrowser) => {
    if (safeBrowser) {
      setShowSEBModal(true);
    } else {
      await createSubmission(
        {
          assignmentId: id,
          submissionId: subId,
        },
        "revise-needed"
      );
      navigate(`/quiz/attempt/${subId}`);
    }
  };

  const handleSubmittedAction = (id) => {
    navigate(`/assignments/submission/${id}`);
  };

  const handleMarkedAction = (id) => {
    navigate(`/assignments/submission/marked/${id}`);
  };

  const hasMore = currentPage < totalPages;

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    AllExercises(currentPage + 1, limit, selectedStatus, selectedSubject, selectedSort, search);
  };

  // Fetch the next page as the sentinel below the table scrolls into view.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, currentPage, limit, selectedStatus, selectedSubject, selectedSort, search]);

  const getLevelBackgroundColor = (level) => {
    switch (level) {
      case "pending":
        return "#C4C4C4"; // grey - Not Attempted
      case "in-progress":
        return "#4DA9F4";
      case "submitted":
        return "#F5C518"; // yellow
      case "marked":
        return "#0FE133"; // green
      case "revise-needed":
        return "#EB3232"; // red - Revise Needed
      case "terminated":
        return "#EB3232";
      case "overdue":
        return "#dbdbdb";
      default:
        return "#B0BEC5";
    }
  };

  const getStatusLabel = (level) => {
    switch (level) {
      case "pending":
        return "Not Attempted";
      case "revise-needed":
        return "Revise Needed";
      default:
        return level.toUpperCase();
    }
  };

  const getButtonLabel = (status) => {
    switch (status) {
      case "pending":
      case "in-progress":
        return "Attempt";
      case "submitted":
        return "View Submission";
      case "marked":
        return "View Remarks";
      case "revise-needed":
        return "Re-Attempt";
      case "terminated":
        return "View Remarks";
      default:
        return "Take Action";
    }
  };

  const applyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    setCurrentPage(1);
    const queryParams = new URLSearchParams({
      page: 1,
      limit,
      search,
      ...appliedFilters
    }).toString();
    navigate(`/assignments?${queryParams}`);
  };

  const handleSortChange = (field, order) => {
    setCurrentPage(1);
    navigate(`/assignments?page=1&limit=${limit}&sortField=${field}&sortOrder=${order}`);
  };

  useEffect(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => {
        console.error("Failed to exit fullscreen:", err);
      });
    }
  }, []);

  // Function to determine the overall status of a parent assignment
function getParentStatus(parentAssignment) {
  const FINAL_STATUSES = ["terminated", "marked", "submitted"]; // priority order
  const children = parentAssignment.childAssignments || [];

  // Helper: an assignment is overdue only if it has a deadline that has passed
  // and it is not already in a final state. Assignments without a deadline
  // (endDate is null/undefined) are never overdue.
  const isOverdue = (endDate, status) =>
    !!endDate &&
    new Date(endDate) < new Date() &&
    !FINAL_STATUSES.includes(status);

  // --- 1. If parent is already final → return immediately
  if (FINAL_STATUSES.includes(parentAssignment.submissionStatus)) {
    return parentAssignment.submissionStatus;
  }

  // --- 2. If no children → check overdue directly
  if (!children.length) {
    return isOverdue(parentAssignment.endDate, parentAssignment.submissionStatus)
      ? "overdue"
      : parentAssignment.submissionStatus;
  }

  // --- 3. Check children for final statuses (priority order)
  for (const status of FINAL_STATUSES) {
    if (children.some((child) => child.submissionStatus === status)) {
      return status;
    }
  }

  // --- 4. Check parent overdue (AFTER children final states but BEFORE pending/in-progress)
  if (isOverdue(parentAssignment.endDate, parentAssignment.submissionStatus)) {
    return "overdue";
  }

  // --- 5. If all children are pending
  const allPending = children.every((child) => child.submissionStatus === "pending");
  if (allPending) {
    return "pending";
  }

  // --- 6. If any child is in-progress
  const hasInProgress = children.some((child) => child.submissionStatus === "in-progress");
  if (hasInProgress) {
    return "in-progress";
  }

  // --- 7. If any child is overdue
  const hasOverdue = children.some((child) =>
    isOverdue(child.endDate, child.submissionStatus)
  );
  if (hasOverdue) {
    return "overdue";
  }

  // --- 8. Fallback: parent's own status
  return parentAssignment.submissionStatus;
}





  // Function to check if an assignment should show dropdown or preview button
  const shouldShowDropdown = (assignment) => {
    // If it's a child assignment, check its status
    if (!assignment.childAssignments || assignment.childAssignments.length === 0) {
      return !["submitted", "marked", "terminated"].includes(assignment.submissionStatus);
    }
    
    // If it's a parent assignment, check if any child is still active (not submitted/marked/terminated)
    return assignment.childAssignments.some(child => 
      !["submitted", "marked", "terminated"].includes(child.submissionStatus)
    );
  };

  // Function to check if assignment is in final state (submitted, marked, terminated)
const isFinalState = (assignment) => {
  const FINAL_STATUSES = ["submitted", "marked", "terminated"];

  // Check parent
  if (FINAL_STATUSES.includes(assignment.submissionStatus)) {
    return true;
  }

  // If children exist, check if ANY child is final
  if (assignment.childAssignments && assignment.childAssignments.length > 0) {
    return assignment.childAssignments.some(child =>
      FINAL_STATUSES.includes(child.submissionStatus)
    );
  }

  // If no children and parent not final
  return false;
};


  // Function to get the appropriate button for final state assignments
const getFinalStateButton = (assignment) => {
  const FINAL_STATUSES = ["terminated", "marked", "submitted"]; // priority order

  // Helper: get status + id of the matching assignment
  const getFinalStatusAndId = (assignment) => {
    // Check parent first
    if (FINAL_STATUSES.includes(assignment.submissionStatus)) {
      return { status: assignment.submissionStatus, id: assignment.submissionId };
    }

    // Check children if any
    if (assignment.childAssignments && assignment.childAssignments.length > 0) {
      for (const status of FINAL_STATUSES) {
        const match = assignment.childAssignments.find(
          (child) => child.submissionStatus === status
        );
        if (match) {
          return { status, id: match.submissionId };
        }
      }
    }

    return { status: null, id: null };
  };

  const { status, id } = getFinalStatusAndId(assignment);

  switch (status) {
    case "submitted":
      return (
        <Button
          variant="primary"
          className="px-3 py-1 border-0 button-style"
          style={{ fontSize: "14px" }}
          onClick={() => handleSubmittedAction(id)}
        >
          View Submission
        </Button>
      );

    case "marked":
      return (
        <Button
          variant="primary"
          className="px-3 py-1 border-0 button-style"
          style={{ fontSize: "14px" }}
          onClick={() => handleMarkedAction(id)}
        >
          View Remarks
        </Button>
      );

    case "terminated":
      return (
        <Button
          variant="danger"
          className="px-3 py-1 border-0 button-style"
          style={{ fontSize: "14px" }}
          onClick={() => setShowTerminatedModal(true)}
        >
          Terminated
        </Button>
      );

    default:
      return null;
  }
};



  return (
    <>
      <div className="d-block d-lg-flex justify-content-between">
        <div>
          <PageHeading heading={t("Assignments")} />
        </div>
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-6" style={{ gridGap: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gridGap: "10px", flexWrap: "wrap" }}>
            <Dropdown>
              <Dropdown.Toggle variant="light" id="dropdown-exercise" style={{ border: "1px solid rgb(185, 185, 185)", textTransform: "capitalize", color: "#323232", backgroundColor: "transparent", borderRadius: "30px", padding: "7px 20px", fontWeight: "500" }}>
                {t(selectedSubject) || t("Filter Subject")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => { setSelectedSubject("All"); AllExercises(1, limit, selectedStatus, "All", selectedSort, search); }} style={{ textTransform: "capitalize" }}>{"All"}</Dropdown.Item>
                {subjects?.map((item, index) => (
                  <Dropdown.Item key={index} onClick={() => { setSelectedSubject(item?.label); AllExercises(1, limit, selectedStatus, item?.label, selectedSort, search); }} style={{ textTransform: "capitalize" }}>{t(item?.label)}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle variant="light" id="dropdown-exercise" style={{ border: "1px solid rgb(185, 185, 185)", textTransform: "capitalize", color: "#323232", backgroundColor: "transparent", borderRadius: "30px", padding: "7px 20px", fontWeight: "500" }}>
                {t(selectedStatus) || t("Filter Status")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {statusOptions?.map((item, index) => (
                  <Dropdown.Item key={index} onClick={() => { setSelectedStatus(item); AllExercises(1, limit, item, selectedSubject, selectedSort, search); }} style={{ textTransform: "capitalize" }}>{t(item)}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle variant="light" id="dropdown-exercise" style={{ border: "1px solid rgb(185, 185, 185)", textTransform: "capitalize", color: "#323232", backgroundColor: "transparent", borderRadius: "30px", padding: "7px 20px", fontWeight: "500" }}>
                {t(selectedSort) || t("Select Sort")}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {sortOptions?.map((item, index) => (
                  <Dropdown.Item key={index} onClick={() => { setSelectedSort(item); AllExercises(1, limit, selectedStatus, selectedSubject, item, search); }}>{t(item)}</Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <Button
              variant="light"
              onClick={handleRefresh}
              disabled={loading}
              title={t("Refresh")}
              aria-label={t("Refresh")}
              style={{ border: "1px solid rgb(185, 185, 185)", color: "#323232", backgroundColor: "transparent", borderRadius: "30px", padding: "7px 16px", fontWeight: "500" }}
            >
              <FontAwesomeIcon icon={faRotateRight} spin={loading} />
              <span className="ms-2">{t("Refresh")}</span>
            </Button>
          </div>
          <div>
            <Form.Group className="position-relative" controlId="formBasicPassword">
              <InputGroup>
                <Form.Control
                  type={"text"}
                  placeholder={t("Search Here...")}
                  className="px-3 py-2 border-right-0"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      AllExercises(1, limit, "", "", "", search);
                    }
                  }}
                  style={{
                    borderTopLeftRadius: "30px",
                    borderBottomLeftRadius: "30px",
                    borderColor: "#ea4f50",
                    borderRight: "0",
                  }}
                />
                <InputGroup.Text
                  id="basic-addon2"
                  className="bg-white"
                  onClick={() => AllExercises(1, limit, "", "", "", search)}
                  style={{
                    cursor: "pointer",
                    borderTopRightRadius: "30px",
                    borderBottomRightRadius: "30px",
                    borderColor: "#ea4f50",
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} style={{ color: "#ea4f50" }} />
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Table responsive="sm" style={{ overflow: "auto" }}>
          <thead>
            <tr>
              <th style={{ color: "#303972" }}>Assignment Name</th>
              <th style={{ color: "#303972" }}>Subject</th>
              {/* <th style={{ color: "#303972" }}>Difficulty Level</th> */}
              <th style={{ color: "#303972" }}>Deadline</th>
              <th style={{ color: "#303972" }}>Status</th>
              <th style={{ color: "#303972" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(Math.min(limit, 5))].map((_, index) => (
                <tr key={index}>
                  <td className="py-3">
                    <div style={{ backgroundColor: "#e0e0e0", height: "20px", width: "80%", borderRadius: "4px" }}></div>
                  </td>
                  <td className="py-3">
                    <div style={{ backgroundColor: "##e0e0e0", height: "20px", width: "40%", borderRadius: "4px" }}></div>
                  </td>
                  {/* <td className="py-3">
                    <div style={{ backgroundColor: "#e0e0e0", height: "20px", width: "40%", borderRadius: "4px" }}></div>
                  </td> */}
                  <td className="py-3">
                    <div style={{ backgroundColor: "#e0e0e0", height: "20px", width: "40%", borderRadius: "4px" }}></div>
                  </td>
                  <td className="py-3">
                    <div style={{ backgroundColor: "#e0e0e0", height: "20px", width: "40%", borderRadius: "4px" }}></div>
                  </td>
                  <td className="py-3">
                    <div style={{ backgroundColor: "#e0e0e0", height: "20px", width: "40%", borderRadius: "4px" }}></div>
                  </td>
                </tr>
              ))
            ) : assignments.length === 0 ? (
              <tr>
                <td colSpan="6" className="py-3 text-center">
                  <Image src={nodataimage} />
                  <p className="pt-2 text-secondary fw-bold fs-5">
                    {t("No assignments found")}
                  </p>
                </td>
              </tr>
            ) : (
              assignments.map((assignment, index) => (
                <React.Fragment key={assignment._id}>
                  <tr>
                    <td className="py-3">
                      <div style={{ color: "#303972", fontWeight: 700, textDecoration: "none" }}>
                        {assignment.title}
                      </div>
                    </td>
                    <td className="py-3" style={{ color: "#A098AE" }}>
                      {assignment?.exercise?.subject?.title || assignment?.studySet?.sourceLanguage?.title}
                    </td>
                    <td className="py-3" style={{ color: "#A098AE" }}>
                      {assignment?.endDate ? moment(assignment?.endDate).format("MMM D, YYYY") : "No Deadline"}
                    </td>
                    <td className="py-3" style={{ color: "#A098AE" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: "30px",
                          backgroundColor: getLevelBackgroundColor(getParentStatus(assignment)),
                          color: "#fff",
                          fontSize: "14px",
                        }}
                      >
                        {getStatusLabel(getParentStatus(assignment))}
                      </span>
                    </td>
                    <td className="py-3">
                      {isFinalState(assignment) ? (
                        getFinalStateButton(assignment)
                      ) : assignment.childAssignments && assignment.childAssignments.length > 0 && shouldShowDropdown(assignment) ? (
                        <Dropdown>
                          <Dropdown.Toggle 
                            variant="light" 
                            size="sm"
                            className="d-flex align-items-center px-3 py-1 border-0"
                            style={{ 
                              fontSize: "14px",
                              backgroundColor: "#f1f1f1",
                              color: "#000",
                              borderRadius: "50px"
                            }}
                          >
                           Attempt
                            {/* <FontAwesomeIcon icon={faChevronDown} className="ms-2" /> */}
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            <Dropdown.Header>Select Difficulty Level</Dropdown.Header>
                            {/* Parent assignment option */}
                            <Dropdown.Item onClick={() => handlePreview(assignment)}>
                              <div className="d-flex align-items-center justify-content-between dropdown-item-content" style={{ gap: "10px" }}>
                                <span>{assignment.exercise?.difficultyLevel}</span>
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "3px 10px",
                                    borderRadius: "30px",
                                    backgroundColor: getLevelBackgroundColor(assignment.submissionStatus),
                                    color: "#fff",
                                    fontSize: "12px",
                                  }}
                                >
                                  {getStatusLabel(assignment.submissionStatus)}
                                </span>
                              </div>
                            </Dropdown.Item>

                            {/* Child assignments */}
                            {assignment.childAssignments.map((child, index) => (
                              <Dropdown.Item key={child._id || index} onClick={() => handlePreview(child)}>
                                <div className="d-flex align-items-center justify-content-between dropdown-item-content" style={{ gap: "10px" }}>
                                  <span>{child.exercise?.difficultyLevel}</span>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "3px 10px",
                                      borderRadius: "30px",
                                      backgroundColor: getLevelBackgroundColor(child.submissionStatus),
                                      color: "#fff",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {getStatusLabel(child.submissionStatus)}
                                  </span>
                                </div>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : (
                        <Button
                          variant="primary"
                          className="px-3 py-1 border-0 button-style"
                          style={{ fontSize: "14px" }}
                          onClick={() => handlePreview(assignment)}
                        >
                          Attempt
                        </Button>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </Table>
      </div>
      {/* Infinite scroll sentinel: entering the viewport loads the next page. */}
      {!loading && hasMore && (
        <div ref={loadMoreRef} className="text-center py-3">
          {loadingMore ? (
            <span className="text-secondary">{t("Loading more...")}</span>
          ) : (
            <Button
              variant="light"
              onClick={loadMore}
              style={{
                border: "1px solid rgb(185, 185, 185)",
                color: "#323232",
                backgroundColor: "transparent",
                borderRadius: "30px",
                padding: "7px 20px",
                fontWeight: "500",
              }}
            >
              {t("Load more")}
            </Button>
          )}
        </div>
      )}
      
      {/* Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedAssignment?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {selectedAssignment && (
            <>
              <div className="mb-3">
                <div className="mb-3">
                  <h6 className="d-flex align-items-center mb-2">
                    <strong>Difficulty Level:</strong>{" "}
                    <span
                      style={{
                        display: "inline-block",
                        marginLeft: "8px",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        backgroundColor:
                          selectedAssignment?.exercise?.difficultyLevel === "Expert"
                            ? "#dc3545"
                            : selectedAssignment?.exercise?.difficultyLevel === "Intermediate"
                            ? "#ffc107"
                            : selectedAssignment?.exercise?.difficultyLevel === "Beginner"
                            ? "#28a745"
                            : "#6c757d",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      {selectedAssignment?.exercise?.difficultyLevel || "No Difficulty Level"}
                    </span>
                  </h6>

                  <h6>
                    <strong>Deadline:</strong>{" "}
                    <span style={{ color: "#FF6B00", fontWeight: "500" }}>
                      {selectedAssignment?.endDate
                        ? moment(selectedAssignment?.endDate).format("MMM D, YYYY")
                        : "No Deadline"}
                    </span>
                  </h6>
                </div>
              </div>
              <h5 className="mb-2">{t("Teacher's Instructions")}</h5>
              {selectedAssignment?.teacherInstructions && (
                <div
                  className="mb-3 p-3"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ whiteSpace: "pre-wrap" }}>{selectedAssignment.teacherInstructions}</div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            className="mt-1 px-4 py-2 border-0 button-style-gray" 
            onClick={() => setShowPreviewModal(false)}
          >
            Close
          </Button>

          <Button
            variant="primary"
            className='mt-1 px-4 py-2 border-0 button-style'
            onClick={() => {
              setShowPreviewModal(false);
              handleAction(
                selectedAssignment?.submissionStatus,
                selectedAssignment?._id,
                selectedAssignment?.submissionId,
                selectedAssignment?.type,
                selectedAssignment?.type === "studySet" ? selectedAssignment?.studySet._id : null,
                selectedAssignment?.safeBrowser
              );
            }}
          >
            {getButtonLabel(selectedAssignment?.submissionStatus)}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <DynamicModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleConfirm();
          }
        }}
        handleConfirm={handleConfirm}
        heading={t("Assignment Instructions")}
        description={t("If you switch tabs, minimize, or leave this tab, your assignment will be immediately terminated.")}
        confirmText={t("Start")}
        denyText={t("Cancel")}
      />
      <DynamicModal
        show={showTerminatedModal}
        handleClose={() => setShowTerminatedModal(false)}
        handleConfirm={() => setShowTerminatedModal(false)}
        heading={t("Assignment Terminated")}
        cancelBtn={false}
        description={t("You cheated or did some suspicious activity (like switching tabs or minimizing the window). As a result, your quiz has been canceled.")}
        confirmText={t("Okay")}
      />
      <SebInstructionsModal
        show={showSEBModal}
        handleClose={() => setShowSEBModal(false)}
        handleDownload={handleDownloadSebFile}
        loadingSeb={loadingSeb}
      />
    </>
  );
};

export default Assignments;